import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ChevronLeft, PlusCircle, Pencil, Trash2, ImageOff, Rocket, Sparkles, Ban, Gift } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { useAuth } from "../context/AuthContext";
import { useCoverUrl } from "../hooks/useCoverUrl";
import { fetchOwnEvents, deleteEvent, cancelEvent } from "../lib/eventApi";
import { startBoostCheckout, getMyBoosts } from "../lib/boostApi";
import { getListingStatus, startListingCheckout, adminActivateBoost, confirmListingPayment, LISTING_PACKS } from "../lib/listingApi";
import { formatCardDate } from "../lib/dates";
import { shareInvite } from "../lib/referral";

// Lazily signs its own display-sized cover (shared cache/dedup via useCoverUrl) so
// the My Events list renders immediately. Events with no cover show the ImageOff
// placeholder exactly as before; the neutral thumbnail bg shows briefly while a
// real cover is being signed.
function MyEventThumb({ event }) {
  const { url: coverUrl, onError: onCoverError } = useCoverUrl(event.coverPath);
  if (!event.coverPath) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <ImageOff className="h-5 w-5 text-white/25" />
      </div>
    );
  }
  return coverUrl ? (
    <img src={coverUrl} alt={event.name} onError={onCoverError} className="h-full w-full object-cover" />
  ) : null;
}

// Derive the display status. "Past" is computed from the date (not stored).
function displayStatus(ev) {
  if (ev.status === "cancelled") return "Cancelled";
  if (ev.status === "hidden") return "Hidden";
  const end = ev.end_at || ev.start_at;
  if (end && new Date(end) < new Date()) return "Past";
  return "Active";
}

const STATUS_STYLES = {
  Active: "bg-[#1f7a3f]/20 text-[#4ade80]",
  Cancelled: "bg-[#ff2a00]/15 text-[#ff6a4d]",
  Hidden: "bg-white/10 text-white/50",
  Past: "bg-white/10 text-white/50",
};

export default function MyEvents() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmId, setConfirmId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmCancelId, setConfirmCancelId] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [boosted, setBoosted] = useState({});
  const [boostingId, setBoostingId] = useState(null);
  const [listing, setListing] = useState(null);
  const [startingCheckout, setStartingCheckout] = useState(false);
  const [buyingPack, setBuyingPack] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  const load = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    // After returning from a listing-credit checkout, verify the session so the
    // purchased credits are counted immediately (webhook is the backstop).
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (sid) {
      await confirmListingPayment(sid);
      window.history.replaceState({}, "", window.location.pathname);
    }
    try {
      setEvents(await fetchOwnEvents(user.id));
      getMyBoosts().then(setBoosted);
      getListingStatus().then(setListing).catch(() => {});
    } catch (err) {
      toast.error("Could not load your events.");
      // eslint-disable-next-line no-console
      console.warn("[RoadRnr] fetchOwnEvents error:", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const isAdmin = !!listing?.is_admin;
  const needsPaid =
    listing && !isAdmin && listing.free_remaining <= 0 && listing.paid_available <= 0;

  const startPaidListing = async () => {
    setStartingCheckout(true);
    try {
      const res = await startListingCheckout();
      if (res?.native) {
        toast.success("Listing credit added");
        await load();
        setStartingCheckout(false);
        return;
      }
      if (res?.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      throw new Error("Could not start checkout.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Could not start checkout.");
      setStartingCheckout(false);
    }
  };

  // Buy a non-expiring listing-credit pack (10 or 20). Reuses the same Stripe
  // Checkout flow as the single listing; the chosen price id selects the pack.
  const buyPack = async (priceId) => {
    setBuyingPack(priceId);
    try {
      const res = await startListingCheckout("/my-events", priceId);
      if (res?.native) {
        toast.success("Listing credits added");
        await load();
        setBuyingPack(null);
        return;
      }
      if (res?.checkout_url) {
        window.location.href = res.checkout_url;
        return;
      }
      throw new Error("Could not start checkout.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Could not start checkout.");
      setBuyingPack(null);
    }
  };

  const doBoost = async (eventId) => {
    setBoostingId(eventId);
    try {
      if (isAdmin) {
        await adminActivateBoost(eventId);
        toast.success("Event boosted for 7 days");
        setBoosted((b) => ({ ...b, [eventId]: true }));
        setBoostingId(null);
        return;
      }
      const res = await startBoostCheckout(eventId);
      if (res?.native) {
        toast.success("Event boosted for 7 days");
        setBoosted((b) => ({ ...b, [eventId]: true }));
        setBoostingId(null);
        return;
      }
      if (res?.checkout_url) {
        window.location.href = res.checkout_url;
      } else {
        throw new Error("No checkout URL returned.");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Could not start Boost checkout.";
      toast.error(msg);
      setBoostingId(null);
    }
  };

  const doDelete = async () => {
    if (!confirmId) return;
    setDeleting(true);
    try {
      const { storageWarning } = await deleteEvent(confirmId);
      toast.success("Event deleted");
      if (storageWarning) toast.warning(storageWarning);
      setEvents((prev) => prev.filter((e) => e.id !== confirmId));
      setConfirmId(null);
    } catch (err) {
      toast.error(err.message || "Could not delete the event.");
    } finally {
      setDeleting(false);
    }
  };

  // Cancel the whole event/series (free): sets status='cancelled' via the existing
  // soft-cancel. No listing/credit/Stripe involvement. Owner still sees it here.
  const doCancel = async () => {
    if (!confirmCancelId) return;
    setCancelling(true);
    try {
      await cancelEvent(confirmCancelId);
      toast.success("Event cancelled");
      setEvents((prev) =>
        prev.map((e) => (e.id === confirmCancelId ? { ...e, status: "cancelled" } : e))
      );
      setConfirmCancelId(null);
    } catch (err) {
      toast.error(err.message || "Could not cancel the event.");
    } finally {
      setCancelling(false);
    }
  };

  if (authLoading || (user && loading)) {
    return (
      <div className="flex min-h-[100dvh] items-center justify-center bg-[#050505]">
        <Loader2 className="h-6 w-6 animate-spin text-[#ff2a00]" />
      </div>
    );
  }
  if (!user) return null;

  return (
    <div data-testid="my-events-page" className="min-h-[100dvh] bg-[#050505] pb-16">
      <div className="sticky top-0 z-30 flex items-center gap-3 border-b border-white/10 bg-[#050505]/85 px-5 pb-4 pt-6 backdrop-blur-xl">
        <button
          data-testid="myevents-back"
          onClick={() => navigate("/profile")}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-[#0d0d0d]"
          aria-label="Back"
        >
          <ChevronLeft className="h-5 w-5 text-white" />
        </button>
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-white">
          My events
        </h1>
      </div>

      <div className="mx-auto max-w-md px-5 pt-5">
        {needsPaid ? (
          <button
            data-testid="myevents-add-paid"
            onClick={startPaidListing}
            disabled={startingCheckout}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#ff2a00] py-3.5 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98] disabled:opacity-60"
          >
            {startingCheckout ? <Loader2 className="h-5 w-5 animate-spin" /> : <><PlusCircle className="h-5 w-5" /> Add another event · £1.99</>}
          </button>
        ) : (
          <button
            data-testid="myevents-add"
            onClick={() => navigate("/add-event")}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-[#ff2a00] py-3.5 text-base font-bold text-white transition-transform duration-200 active:scale-[0.98]"
          >
            <PlusCircle className="h-5 w-5" /> Add Event
          </button>
        )}
        {!isAdmin && listing && (
          <div
            data-testid="listing-credits-panel"
            className="mb-5 mt-3 rounded-2xl border border-white/10 bg-[#0d0d0d] p-4"
          >
            <p data-testid="free-remaining" className="text-sm text-white/70">
              You have{" "}
              <span className="font-bold text-white">{listing.free_remaining ?? 0}</span>{" "}
              free {(listing.free_remaining ?? 0) === 1 ? "listing" : "listings"} remaining this month
            </p>
            {listing.paid_available > 0 && (
              <p data-testid="purchased-listings" className="mt-1 text-sm text-white/70">
                Purchased listings:{" "}
                <span className="font-bold text-[#ff2a00]">{listing.paid_available}</span>
              </p>
            )}
            {listing.founding_remaining > 0 && (
              <p data-testid="myevents-founding" className="mt-1 text-xs text-[#FFB000]">
                Founding organiser credits: {listing.founding_remaining} remaining
              </p>
            )}
            {/* Referral prompt — same existing invite link/native share as Profile */}
            <button
              type="button"
              data-testid="myevents-referral"
              onClick={shareInvite}
              className="mt-4 flex w-full items-start gap-2 rounded-xl border border-[#ff2a00]/20 bg-[#ff2a00]/5 px-3 py-2.5 text-left"
            >
              <Gift className="mt-0.5 h-4 w-4 shrink-0 text-[#ff2a00]" />
              <span className="flex-1">
                <span className="block text-xs font-bold uppercase tracking-wide text-white/80">Get 3 free listings</span>
                <span className="mt-0.5 block text-xs text-white/50">
                  Invite a friend and you'll both get 3 when they publish their first event.
                </span>
                <span className="mt-1 block text-xs font-semibold text-[#ff2a00]">Share invite link →</span>
              </span>
            </button>
            <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-white/40">
              Need more listings?
            </p>
            <div className="mt-2 grid grid-cols-2 gap-3">
              {LISTING_PACKS.filter((p) => p.credits >= 10).map((p) => (
                <button
                  key={p.priceId}
                  data-testid={`buy-pack-${p.credits}`}
                  onClick={() => buyPack(p.priceId)}
                  disabled={!!buyingPack}
                  className="flex flex-col items-center rounded-2xl border border-white/10 bg-[#121212] px-3 py-3 transition-transform duration-200 active:scale-[0.98] disabled:opacity-60"
                >
                  {buyingPack === p.priceId ? (
                    <Loader2 className="h-5 w-5 animate-spin text-[#ff2a00]" />
                  ) : (
                    <>
                      <span className="text-sm font-bold text-white">Buy {p.credits}</span>
                      <span className="mt-0.5 text-xs text-[#ff2a00]">{p.price}</span>
                    </>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
        {isAdmin && <div className="mb-5" />}

        {events.length > 0 && (
          <p data-testid="myevents-count" className="mb-3 text-xs text-white/40">
            {events.length} {events.length === 1 ? "event" : "events"}
          </p>
        )}
        {events.length === 0 ? (
          <p data-testid="myevents-empty" className="mt-10 text-center text-sm text-white/40">
            You haven't listed any events yet.
          </p>
        ) : (
          <div className="space-y-3">
            {events.map((ev) => {
              const status = displayStatus(ev);
              return (
                <div
                  key={ev.id}
                  data-testid={`myevent-${ev.id}`}
                  className="overflow-hidden rounded-2xl border border-white/10 bg-[#0d0d0d]"
                >
                  <div className="flex gap-3 p-3">
                    <div className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-[#1a1a1a]">
                      <MyEventThumb event={ev} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className="truncate text-[15px] font-bold text-white">{ev.name}</h3>
                        <span
                          data-testid={`myevent-status-${ev.id}`}
                          className={`shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${STATUS_STYLES[status]}`}
                        >
                          {status}
                        </span>
                      </div>
                      <p className="mt-0.5 text-xs font-medium text-[#ff2a00]">
                        {formatCardDate({
                          start_date: ev.start_at ? ev.start_at.split("T")[0] : "",
                          end_date: ev.end_at ? ev.end_at.split("T")[0] : "",
                          date: "",
                        })}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-white/50">
                        {ev.venue_name || ev.address || ev.postcode || "Location TBA"}
                      </p>
                    </div>
                  </div>
                  {status === "Active" && (
                    boosted[ev.id] ? (
                      <div
                        data-testid={`myevent-boosted-${ev.id}`}
                        className="flex items-center justify-center gap-2 border-t border-white/10 bg-[#ff2a00]/10 py-2.5 text-sm font-semibold text-[#ff6a4d]"
                      >
                        <Sparkles className="h-4 w-4" /> BOOSTED · {(() => {
                          const ms = new Date(boosted[ev.id]).getTime() - Date.now();
                          if (!Number.isFinite(ms) || ms <= 0) return "active";
                          const d = Math.floor(ms / 86400000);
                          const h = Math.floor((ms % 86400000) / 3600000);
                          const m = Math.floor((ms % 3600000) / 60000);
                          return d >= 1 ? `${d}d ${h}h left` : h >= 1 ? `${h}h ${m}m left` : `${m}m left`;
                        })()}
                      </div>
                    ) : (
                      <button
                        data-testid={`myevent-boost-${ev.id}`}
                        onClick={() => doBoost(ev.id)}
                        disabled={boostingId === ev.id}
                        className="flex w-full items-center justify-center gap-2 border-t border-white/10 py-2.5 text-sm font-bold text-[#ff2a00] transition-colors hover:bg-[#ff2a00]/5 disabled:opacity-60"
                      >
                        {boostingId === ev.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <Rocket className="h-4 w-4" /> {isAdmin ? "Boost event · Free for 7 days" : "Boost event · £4.99 for 7 days"}
                          </>
                        )}
                      </button>
                    )
                  )}
                  <div className="flex border-t border-white/10">
                    <button
                      data-testid={`myevent-edit-${ev.id}`}
                      onClick={() => navigate(`/edit-event/${ev.id}`)}
                      className="flex flex-1 items-center justify-center gap-2 py-3 text-sm font-semibold text-white/80 transition-colors hover:bg-white/5"
                    >
                      <Pencil className="h-4 w-4" /> Edit
                    </button>
                    {status === "Active" && (
                      <button
                        data-testid={`myevent-cancel-${ev.id}`}
                        onClick={() => setConfirmCancelId(ev.id)}
                        className="flex flex-1 items-center justify-center gap-2 border-l border-white/10 py-3 text-sm font-semibold text-white/70 transition-colors hover:bg-white/5"
                      >
                        <Ban className="h-4 w-4" /> Cancel
                      </button>
                    )}
                    <button
                      data-testid={`myevent-delete-${ev.id}`}
                      onClick={() => setConfirmId(ev.id)}
                      className="flex flex-1 items-center justify-center gap-2 border-l border-white/10 py-3 text-sm font-semibold text-[#ff6a4d] transition-colors hover:bg-white/5"
                    >
                      <Trash2 className="h-4 w-4" /> Delete
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AlertDialog open={!!confirmId} onOpenChange={(o) => !o && setConfirmId(null)}>
        <AlertDialogContent className="border-white/10 bg-[#111] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this event?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50">
              This permanently removes the event and its cover image. It will
              disappear from Discover, the map and your events. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/15 bg-transparent text-white hover:bg-white/10">
              Keep event
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="myevents-delete-confirm"
              onClick={(e) => {
                e.preventDefault();
                doDelete();
              }}
              disabled={deleting}
              className="bg-[#ff2a00] text-white hover:bg-[#ff2a00]/90"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete event"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmCancelId} onOpenChange={(o) => !o && setConfirmCancelId(null)}>
        <AlertDialogContent className="border-white/10 bg-[#111] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel this event?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/50">
              It will no longer appear in Discover, Search or on the map.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/15 bg-transparent text-white hover:bg-white/10">
              Keep event
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="myevents-cancel-confirm"
              onClick={(e) => {
                e.preventDefault();
                doCancel();
              }}
              disabled={cancelling}
              className="bg-[#ff2a00] text-white hover:bg-[#ff2a00]/90"
            >
              {cancelling ? <Loader2 className="h-4 w-4 animate-spin" /> : "Cancel event"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
